import { Controller } from '@hotwired/stimulus';
import ltLocale from '@fullcalendar/core/locales/lt';
import WindowResizeObserver from '../utilities/window-resize-observer';

export default class extends Controller {
    static values = {
        title: String,
        timezone: String,
        start: String,
        finish: String,
        markPresent: Array,
        markStopping: Array,
        markUpcoming: Array,
    }

    connect() {
        this.load();
        this.resizeObserver = new ResizeObserver(() => this.adjustCalendarSize());
        this.resizeObserver.observe(this.element);
    }

    disconnect() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    async load() {
        const Calendar = (await import('@fullcalendar/core')).Calendar;
        const dayGridPlugin = (await import('@fullcalendar/daygrid')).default;

        const calendar = new Calendar(this.element, {
            customButtons: {
                prev: {
                    text: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
                    click: function() {
                        calendar.prev();
                    }
                },
                next: {
                    text: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
                    click: function() {
                        calendar.next();
                    }
                }
            },
            plugins: [dayGridPlugin],
            locales: [ltLocale],
            locale: 'lt',
            initialView: 'dayGridMonth',
            headerToolbar: {
                left: 'prev',
                center: 'title',
                right: 'next',
            },
            titleFormat: { year: 'numeric', month: 'long' },
            dayHeaderFormat: { weekday: 'short' },
            fixedWeekCount: false,
            validRange: {
                start: this.startValue,
                end: this.finishValue
            },
            height: '100%',
            aspectRatio: 1,
            dayCellContent: (args) => {
                const dayOfMonth = args.dayNumberText;
                const dateArray = args.date.toISOString().split('T')[0].split('-');
                const dateString = `${dateArray[0]}-${dateArray[1]}-${dateArray[2]}`;
                return { html: `<div class="fc-daygrid-day-number" data-date="${dateString}">${dayOfMonth}</div>` };
            },
            datesSet: () => {
                this.applyCustomStyling();
            }
        });
        calendar.render();
        this.adjustCalendarSize();

        const titleElement = document.createElement('span');
        const titleStyles = ['lg:fs-r', 'fw-bold', 'sm:fs-h5', 'text-primary', 'text-uppercase', 'md:fs-h5']
        titleElement.classList.add(...titleStyles);
        titleElement.textContent = this.titleValue;
        this.element.insertBefore(titleElement, this.element.firstChild);

        WindowResizeObserver.observe((width) => {
            if (width <= 991) {
                calendar.updateSize();
            }
        });
    }

    applyCustomStyling() {
        this.styleUpcomingDates();
        this.styleStoppingDates();
        this.stylePresentDates();
        this.styleHolidayDates();
    }

    styleHolidayDates() {
        const startYear = parseInt(this.startValue.substring(0, 4));
        const endYear = parseInt(this.finishValue.substring(0, 4));
        let holidayDates = [];

        for (let year = startYear; year <= endYear; year++) {

            // easter
            const easterDate = this.getEasterDate(year);
            let secondEasterDate = new Date(easterDate);
            secondEasterDate.setDate(secondEasterDate.getDate() + 1);
            secondEasterDate = secondEasterDate.toISOString().substring(0, 10);

            // mothers day
            const mothersDayDate = this.getMothersDayDate(year);

            // fathers day
            const fathersDayDate = this.getFathersDayDate(year);

            holidayDates.push(
                `${year}-01-01`,
                `${year}-02-16`,
                `${year}-03-11`,
                `${year}-05-01`,
                `${year}-06-24`,
                `${year}-07-06`,
                `${year}-08-15`,
                `${year}-11-01`,
                `${year}-11-02`,
                `${year}-12-24`,
                `${year}-12-25`,
                `${year}-12-26`,
                easterDate,
                secondEasterDate,
                mothersDayDate,
                fathersDayDate
            );
        }

        holidayDates.forEach(date => {
            const el = this.element.querySelector(`#${this.element.id} [data-date="${date}"] .fc-daygrid-day-number`);
            if (el) {
                el.classList.add('mark-holiday');
            }
        });
    }

    stylePresentDates() {
        this.markPresentValue.forEach(date => {
            const el = this.element.querySelector(`#${this.element.id} [data-date="${date}"] .fc-daygrid-day-number`);
            if (el) {
                el.classList.add('mark-present');
                el.classList.remove('mark-upcoming');
                el.classList.remove('mark-absent');
            }
        });
    }

    styleUpcomingDates() {
        let occurrences;
        try {
            occurrences = typeof this.markUpcomingValue === 'string'
                ? JSON.parse(this.markUpcomingValue)
                : this.markUpcomingValue;
        } catch (e) {
            occurrences = [];
        }

        const timezone = this.timezoneValue;
        const todayStr = this.formatDateForFullCalendar(new Date(), timezone);

        const startDate = new Date(`${this.startValue}T00:00:00`);
        const endDate = new Date(`${this.finishValue}T00:00:00`);
        let currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            const dayOfWeek = currentDate.getDay();
            const nsoftDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;

            if (occurrences.some(o => Number(o.day_num) === nsoftDayOfWeek)) {
                const dateString = this.formatDateForFullCalendar(currentDate, timezone);
                const el = this.element.querySelector(
                    `#${this.element.id} [data-date="${dateString}"] .fc-daygrid-day-number,
                 #${this.element.id} .fc-daygrid-day-number[data-date="${dateString}"]`
                );

                if (el) {
                    if (dateString > todayStr) {
                        el.classList.add('mark-upcoming');
                    } else {
                        this.markAbsentIfNecessary(el, dateString);
                    }
                }
            }

            currentDate.setDate(currentDate.getDate() + 1);
        }
    }

    styleStoppingDates() {
        let stoppingPeriods = this.markStoppingValue;

        if (typeof stoppingPeriods === 'string') {
            try {
                stoppingPeriods = JSON.parse(stoppingPeriods);
            } catch (e) {
                console.error('Error parsing stopping periods:', e);
                return;
            }
        }

        const timezone = this.timezoneValue;

        stoppingPeriods.forEach(period => {
            let currentDate = this.getDateInTimezone(new Date(period.from), timezone);
            const endDate = this.getDateInTimezone(new Date(period.to), timezone);

            while (currentDate <= endDate) {
                const dateString = this.formatDateForFullCalendar(currentDate, timezone);
                const el = this.element.querySelector(`[data-date="${dateString}"] .fc-daygrid-day-number`);
                if (el) {
                    if (el.classList.contains('mark-upcoming')) {
                        el.classList.add('mark-stopping')
                        el.classList.remove('mark-upcoming')
                    } else {
                        el.classList.add('mark-stopping')
                    }
                }

                currentDate.setDate(currentDate.getDate() + 1);
            }
        });
    }

    adjustCalendarSize() {
        if (this.calendar) {
            const width = this.element.offsetWidth;
            const height = this.element.offsetHeight;
            const size = Math.min(width, height);

            this.element.style.width = `${size}px`;
            this.element.style.height = `${size}px`;

            this.calendar.updateSize();
        }
    }

    markAbsentIfNecessary(element, dateString) {
        if (!element.classList.contains('mark-present')) {
            element.classList.add('mark-absent');
            element.classList.remove('mark-upcoming');
        }
    }

    getDateInTimezone(date, timezone) {
        return new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    }

    formatDateForFullCalendar(date, timezone) {
        return date.toLocaleDateString('en-CA', { timeZone: timezone });
    }

    getEasterDate(year) {
        const f = Math.floor,
            // Golden Number - 1
            G = year % 19,
            C = f(year / 100),
            // related to Epact
            H = (C - f(C / 4) - f((8 * C + 13)/25) + 19 * G + 15) % 30,
            // number of days from 21 March to the Paschal full moon
            I = H - f(H/28) * (1 - f(29/(H + 1)) * f((21-G)/11)),
            // weekday for the Paschal full moon
            J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7,
            // number of days from 21 March to the Sunday on or before the Paschal full moon
            L = I - J,
            month = 3 + f((L + 40)/44),
            day = L + 28 - 31 * f(month / 4);

        return year + '-' + (month < 10 ? '0' : '') + month + '-' + (day < 10 ? '0' : '') + day;
    }

    getMothersDayDate(year) {
        const date = new Date(year, 4, 1);

        while (date.getDay() !== 0) {
            date.setDate(date.getDate() + 1);
        }

        const month = date.getMonth() + 1;
        const day = date.getDate();

        return `${year}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}`;
    }

    getFathersDayDate(year) {
        const date = new Date(year, 5, 1);

        while (date.getDay() !== 0) {
            date.setDate(date.getDate() + 1);
        }

        const month = date.getMonth() + 1;
        const day = date.getDate();

        return `${year}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}`;
    }
}
